import { LevelConfig, LEVELS } from '@/levels/LevelData';
import { Fleet, MissionOutcome, MissionReport } from '@/fleet/Fleet';
import { LEGACY_LEVEL_FLEETS } from '@/fleet/LegacyFleets';

const STORAGE_KEY = 'spaceGameProgress';

export interface GameProgress {
  currentLevel: number;
  levelsCompleted: number[];
  totalEnemiesKilled: number;
}

export class ProgressManager {
  private progress: GameProgress;
  private fleet: Fleet;

  constructor() {
    this.progress = this.loadProgress();
    this.fleet = this.loadFleet();
  }

  /**
   * Loads the saved roster. Saves from before the roster existed (or with a
   * corrupted roster) get the fleet their highest unlocked level used to field.
   */
  private loadFleet(): Fleet {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const fleet = saved ? Fleet.fromData(JSON.parse(saved).fleet) : null;
      if (fleet) return fleet;
    } catch {
      // Fall through to a starting roster
    }
    return Fleet.fromClasses(LEGACY_LEVEL_FLEETS[this.getHighestUnlockedLevel()]);
  }

  private loadProgress(): GameProgress {
    const defaults: GameProgress = {
      currentLevel: 1,
      levelsCompleted: [],
      totalEnemiesKilled: 0
    };

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return defaults;

      const parsed = JSON.parse(saved) as Partial<GameProgress>;
      return {
        currentLevel: typeof parsed.currentLevel === 'number' ? parsed.currentLevel : defaults.currentLevel,
        levelsCompleted: Array.isArray(parsed.levelsCompleted) ? parsed.levelsCompleted : [],
        totalEnemiesKilled: typeof parsed.totalEnemiesKilled === 'number' ? parsed.totalEnemiesKilled : 0
      };
    } catch {
      // Corrupted save - start fresh rather than crashing the game on load
      return defaults;
    }
  }

  saveProgress(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...this.progress, fleet: this.fleet.toData() }));
  }

  getFleet(): Fleet {
    return this.fleet;
  }

  /** Applies a finished mission to the roster and saves it. */
  recordMission(report: MissionReport): MissionOutcome {
    const outcome = this.fleet.applyMission(report);
    this.saveProgress();
    return outcome;
  }

  /** The furthest level the player can play; it decides which ship classes can be built. */
  getHighestUnlockedLevel(): number {
    return LEVELS.filter(level => this.isLevelUnlocked(level.id)).reduce((max, level) => Math.max(max, level.id), 1);
  }

  isLevelUnlocked(levelId: number): boolean {
    if (levelId === 1) return true;
    return this.progress.levelsCompleted.includes(levelId - 1);
  }

  completeLevel(levelId: number): void {
    if (!this.progress.levelsCompleted.includes(levelId)) {
      this.progress.levelsCompleted.push(levelId);
      this.saveProgress();
    }
  }

  getCurrentLevel(): number {
    return this.progress.currentLevel;
  }

  setCurrentLevel(levelId: number): void {
    this.progress.currentLevel = levelId;
    this.saveProgress();
  }

  addEnemyKill(): void {
    this.progress.totalEnemiesKilled++;
    this.saveProgress();
  }

  getTotalEnemiesKilled(): number {
    return this.progress.totalEnemiesKilled;
  }

  getLevelConfigs(): LevelConfig[] {
    return LEVELS.map(level => ({
      ...level,
      unlocked: this.isLevelUnlocked(level.id),
      completed: this.progress.levelsCompleted.includes(level.id)
    }));
  }

  resetProgress(): void {
    this.progress = {
      currentLevel: 1,
      levelsCompleted: [],
      totalEnemiesKilled: 0
    };
    this.fleet = Fleet.fromClasses([]);
    this.saveProgress();
  }
}